/**
 * DecisionFlow — PlanModifierChecklist
 * ui/src/components/tenant-admin/PlanModifierChecklist.jsx
 * S690 — OI-PATH-B-UX-SIMPLIFICATION
 *
 * Replaces the flat 28-card PlanTypeModifierGrid when applicabilityRows are loaded
 * for a selected component. Shows only type-relevant modifiers in three sections:
 *   REQUIRED   — always visible, must configure before submit
 *   OPTIONAL   — toggle show/hide
 *   NOT APPLICABLE — collapsed, count only
 *
 * Each row expands inline to mount the manager — no scrolling past 28 cards.
 * Progress bar in header: configured / required count.
 *
 * Spec authority: mastercontext_s689.md OI-PATH-B-UX-SIMPLIFICATION
 *
 * Props contract:
 *   applicabilityRows  {array}    — from GET /plan-components/{id}/modifier-applicability
 *                                   Shape: [{ modifier_type, applicability, modifier_status }]
 *   onCardClick        {function} — (modifierType: string) => void  (same as PlanTypeModifierGrid)
 *   isLocked           {bool}     — from isReadOnly && !hasOpenAmendment
 *   planId             {int|null}
 *   planComponentId    {int|null}
 *   period             {object}
 *   mode               {string}   — 'admin' | 'wizard'
 *   readOnly           {bool}
 *   MANAGER_MAP        {object}   — passed from PlanDetailView to avoid re-importing all managers
 *   MODIFIER_CARDS     {array}    — passed from PlanDetailView for label lookup
 *   GRID_KEY_TO_MODIFIER {object} — passed from PlanDetailView for key translation
 *
 * CSS: rgb(var(--color-*)) tokens only. Zero hardcoded colors.
 *
 * OWNERSHIP RULES:
 *   - Does NOT fetch data. PlanDetailView owns all data fetching.
 *   - Does NOT mount managers independently — uses onCardClick for external manager region,
 *     OR expands inline when ActiveManager is available via MANAGER_MAP.
 *   - All amendment state stays in PlanDetailView.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle, Circle } from 'lucide-react';

// ---------------------------------------------------------------------------
// Card label lookup — maps modifier_type (P3B-*/P4-* codes from applicabilityRows)
// to human-readable labels and CARD_DEFS keys.
// This mirrors the GRID_KEY_TO_MODIFIER mapping but in reverse, plus a label map.
// ---------------------------------------------------------------------------

const MODIFIER_TYPE_TO_LABEL = {
  // P3B Performance / Structural
  'P3B-CAP':      { label: 'Accelerator Cap',        gridKey: 'accelerator_caps'    },
  'P3B-KICKER':   { label: 'Kicker / Multiplier',    gridKey: 'kicker_rules'        },
  'P3B-REVTYPE':  { label: 'Revenue Type Differentials', gridKey: 'revenue_type_rules'  },
  'P3B-CLAWBACK': { label: 'Clawback Policy',            gridKey: 'churn_clawback'      },
  'P3B-DRAW':     { label: 'Draw & Guarantee',           gridKey: 'draw'                },
  'P3B-SPLIT':    { label: 'Deal Splits',                gridKey: 'deal-split'          },
  'P3B-SPIFF':    { label: 'SPIFF / Tactical',           gridKey: 'spiff'               },
  'P3B-TXN':      { label: 'Transaction Commission',     gridKey: 'transaction'         },
  'P3B-OVERLAY':  { label: 'Overlay Crediting',          gridKey: 'overlay_attribution' },
  // P4 Strategic
  'P4-TERRITORY': { label: 'Territory Compensation', gridKey: 'territory_compensation' },
  'P4-MARKET':    { label: 'Market Share Incentive',  gridKey: 'market_share'           },
  'P4-PHARMA':    { label: 'Script-Based Incentive', gridKey: 'pharma_script'          },
  'P4-VOLUME':    { label: 'Volume / Quantity',      gridKey: 'volume_compensation'    },
  'P4-RETENTION': { label: 'GRR / NRR Retention',   gridKey: 'grr_nrr_retention'      },
  'P4-POD':       { label: 'Pod / Team',             gridKey: 'pod_compensation'       },
  'P4-RANKING':   { label: 'Ranking / Relative',     gridKey: 'ranking_plans'          },
  'P4-MARGIN':    { label: 'Margin / Profit',        gridKey: 'margin_compensation'    },
  'P4-MULTIYEAR': { label: 'Multi-Year Deal',        gridKey: 'multi_year_deals'       },
  'P4-BONUS':     { label: 'Shared Bonus Pool',      gridKey: 'bonus_pool'             },
  'P4-CONTEST':   { label: 'Contest Programs',       gridKey: 'contest_programs'       },
  'P4-NONCASH':   { label: 'Non-Cash Incentives',   gridKey: 'non_cash_awards'        },
  'P4-TRUEUP':    { label: 'Retroactive True-Up',    gridKey: 'true_up_policy'         },
  'P4-COSELL':    { label: 'Co-Sell / Channel',      gridKey: 'cosell_framework'       },
  'P4-CHANNEL':   { label: 'Channel / Partner',      gridKey: 'channel_compensation'   },
};

// ---------------------------------------------------------------------------
// GROUP_ORDER — Compensation lifecycle sort order within REQUIRED / OPTIONAL sections
// Spec authority: comp_policies_spec_v2_11.md S26.3
// Groups: Core Earnings → Credit & Attribution → Performance Multipliers →
//         Financial Adjustments → Special Incentives
// Unlisted modifier_types default to group 99 (sort to end).
// ---------------------------------------------------------------------------

const GROUP_ORDER = {
  // Group 1 — Core Earnings
  'P3B-TXN':      1,
  'P3B-REVTYPE':  1,
  'P4-VOLUME':    1,
  'P4-MARKET':    1,
  'P4-PHARMA':    1,
  'P4-MARGIN':    1,
  'P4-RETENTION': 1,
  // Group 2 — Credit & Attribution
  'P3B-SPLIT':    2,
  'P3B-OVERLAY':  2,
  'P4-COSELL':    2,
  'P4-CHANNEL':   2,
  'P4-TERRITORY': 2,
  'P4-POD':       2,
  // Group 3 — Performance Multipliers
  'P3B-KICKER':   3,
  'P3B-CAP':      3,
  'P4-MULTIYEAR': 3,
  // Group 4 — Financial Adjustments
  'P3B-DRAW':     4,
  'P3B-CLAWBACK': 4,
  'P4-TRUEUP':    4,
  // Group 5 — Special Incentives
  'P3B-SPIFF':    5,
  'P4-CONTEST':   5,
  'P4-BONUS':     5,
  'P4-NONCASH':   5,
};

function sortByGroup(rows) {
  return [...rows].sort(
    (a, b) => (GROUP_ORDER[a.modifier_type] ?? 99) - (GROUP_ORDER[b.modifier_type] ?? 99)
  );
}

// ---------------------------------------------------------------------------
// Status display
// ---------------------------------------------------------------------------

const STATUS_META = {
  not_started:      { label: 'Not configured',   color: 'text-[rgb(var(--color-text-muted))]',  dot: 'bg-[rgb(var(--color-border))]'   },
  draft:            { label: 'Draft',            color: 'text-[rgb(var(--color-warning))]',     dot: 'bg-[rgb(var(--color-warning))]'  },
  pending_approval: { label: 'Pending approval', color: 'text-[rgb(var(--color-info))]',        dot: 'bg-[rgb(var(--color-info))]'     },
  approved:         { label: 'Approved',         color: 'text-[rgb(var(--color-success))]',     dot: 'bg-[rgb(var(--color-success))]'  },
  rejected:         { label: 'Rejected',         color: 'text-[rgb(var(--color-danger))]',      dot: 'bg-[rgb(var(--color-danger))]'   },
};

function StatusBadge({ status }) {
  const meta = STATUS_META[status] ?? STATUS_META.not_started;
  return (
    <div className="flex items-center gap-1.5 shrink-0">
      <span className={`h-2 w-2 rounded-full shrink-0 ${meta.dot}`} />
      <span className={`text-tok-xs font-medium ${meta.color}`}>{meta.label}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Progress bar
// ---------------------------------------------------------------------------

function ProgressBar({ configured, total }) {
  const pct = total === 0 ? 100 : Math.round((configured / total) * 100);
  const allDone = configured >= total && total > 0;

  return (
    <div className="flex items-center gap-3">
      <div className="flex-1 h-1.5 rounded-full bg-[rgb(var(--color-border))] overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-300 ${allDone ? 'bg-[rgb(var(--color-success))]' : 'bg-[rgb(var(--color-primary))]'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
      <span className={`text-tok-xs font-medium shrink-0 ${allDone ? 'text-[rgb(var(--color-success))]' : 'text-[rgb(var(--color-text-muted))]'}`}>
        {configured}/{total} required configured
      </span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Section header
// ---------------------------------------------------------------------------

function SectionHeader({ label, count, badge, defaultOpen = true, children }) {
  const [open, setOpen] = useState(defaultOpen);

  return (
    <div className="rounded-xl border border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface))] overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left hover:bg-[rgb(var(--color-surface-muted))] transition-colors"
      >
        {open
          ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />
          : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />}
        <span className="text-tok-xs font-semibold uppercase tracking-wider text-[rgb(var(--color-text-muted))]">
          {label}
        </span>
        {count != null && (
          <span className="rounded-full bg-[rgb(var(--color-surface-muted))] px-2 py-0.5 text-tok-xs text-[rgb(var(--color-text-muted))]">
            {count}
          </span>
        )}
        {badge && (
          <span className="ml-auto text-tok-xs text-[rgb(var(--color-text-muted))]">{badge}</span>
        )}
      </button>

      {open && children && (
        <div className="border-t border-[rgb(var(--color-border))] divide-y divide-[rgb(var(--color-border))]">
          {children}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Checklist row — expandable inline manager
// ---------------------------------------------------------------------------

function ChecklistRow({
  row,
  isExpanded,
  onToggle,
  isLocked,
  planId,
  planComponentId,
  period,
  mode,
  readOnly,
  MANAGER_MAP,
  MODIFIER_CARDS,
  GRID_KEY_TO_MODIFIER,
}) {
  const meta = MODIFIER_TYPE_TO_LABEL[row.modifier_type] ?? { label: row.modifier_type, gridKey: null };
  const status = row.modifier_status ?? 'not_started';
  const isConfigured = status === 'approved';

  // GAP-APPROVED-CARD-UX: show feedback banner when approved card is clicked while locked/read-only.
  const [showApprovedFeedback, setShowApprovedFeedback] = useState(false);

  // Resolve the manager for this modifier type
  const gridKey = meta.gridKey;
  const modifierCardKey = gridKey ? (GRID_KEY_TO_MODIFIER[gridKey] ?? gridKey) : null;
  const ActiveManager = modifierCardKey ? MANAGER_MAP[modifierCardKey] : null;

  function handleClick() {
    if (isLocked) return;
    // GAP-APPROVED-CARD-UX: approved + readOnly → show feedback, still allow expand to view
    if (isConfigured && readOnly) {
      setShowApprovedFeedback((v) => !v);
      return;
    }
    onToggle(row.modifier_type);
  }

  return (
    <div>
      {/* Row header */}
      <div
        role="button"
        tabIndex={isLocked ? -1 : 0}
        onClick={handleClick}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleClick()}
        className={[
          'flex items-center gap-3 px-4 py-3 transition-colors',
          isLocked
            ? 'cursor-default opacity-75'
            : 'cursor-pointer hover:bg-[rgb(var(--color-surface-muted))]',
          isExpanded ? 'bg-[rgb(var(--color-primary))]/5' : '',
        ].join(' ')}
      >
        {/* Configured indicator */}
        <div className="shrink-0">
          {isConfigured
            ? <CheckCircle className="h-4 w-4 text-[rgb(var(--color-success))]" />
            : <Circle className="h-4 w-4 text-[rgb(var(--color-border))]" />}
        </div>

        {/* Label */}
        <span className={`flex-1 text-tok-sm font-medium ${isExpanded ? 'text-[rgb(var(--color-primary))]' : 'text-[rgb(var(--color-text))]'}`}>
          {meta.label}
        </span>

        {/* Status badge */}
        <StatusBadge status={status} />

        {/* Expand chevron */}
        {!isLocked && (
          isExpanded
            ? <ChevronDown className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />
            : <ChevronRight className="h-3.5 w-3.5 shrink-0 text-[rgb(var(--color-text-muted))]" />
        )}
      </div>

      {/* GAP-APPROVED-CARD-UX: approved + readOnly feedback banner */}
      {showApprovedFeedback && isConfigured && readOnly && (
        <div className="border-t border-[rgb(var(--color-success))]/20 bg-[rgb(var(--color-success))]/5 px-4 py-3 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 rounded-full bg-[rgb(var(--color-success))] shrink-0" />
            <p className="text-tok-xs text-[rgb(var(--color-success))]">
              This modifier is approved. To modify it, create an amendment from the plan header.
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowApprovedFeedback(false); }}
            className="shrink-0 text-tok-xs text-[rgb(var(--color-text-muted))] hover:text-[rgb(var(--color-text))] underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Inline manager — expands in place */}
      {isExpanded && !isLocked && ActiveManager && (
        <div className="border-t border-[rgb(var(--color-primary))]/20 bg-[rgb(var(--color-surface-muted))] px-4 py-4">
          <ActiveManager
            mode={mode ?? 'admin'}
            period={period}
            wizardSteps={[]}
            embedded={true}
            planId={planId}
            planComponentId={planComponentId}
            fiscalPeriodId={period?.fiscal_period_id ?? null}
            readOnly={readOnly}
          />
        </div>
      )}

      {/* Fallback: no manager available for this modifier type */}
      {isExpanded && !isLocked && !ActiveManager && (
        <div className="border-t border-[rgb(var(--color-border))] bg-[rgb(var(--color-surface-muted))] px-4 py-4">
          <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">
            Configuration for <strong className="text-[rgb(var(--color-text))]">{meta.label}</strong> is not yet available in this view.
          </p>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export default function PlanModifierChecklist({
  applicabilityRows = [],
  onCardClick,
  onExpandedChange,   // GAP-UNSAVED-PROTECTION: called with modifierType|null on expand/collapse
  isLocked = false,
  planId,
  planComponentId,
  period,
  mode,
  readOnly,
  MANAGER_MAP = {},
  MODIFIER_CARDS = [],
  GRID_KEY_TO_MODIFIER = {},
}) {
  const [expandedKey, setExpandedKey] = useState(null);
  const [showOptional, setShowOptional] = useState(true);

  function handleToggle(modifierType) {
    setExpandedKey((prev) => {
      const next = prev === modifierType ? null : modifierType;
      // GAP-UNSAVED-PROTECTION: notify parent of expand/collapse so wizard nav
      // can warn if CA navigates away while a modifier is open (spec S695 S4).
      if (onExpandedChange) onExpandedChange(next);
      return next;
    });
    // Also fire the external onCardClick so PlanDetailView can update activeModifier
    // for any external manager region logic — belt-and-suspenders.
    if (onCardClick) {
      const meta = MODIFIER_TYPE_TO_LABEL[modifierType];
      if (meta?.gridKey) onCardClick(meta.gridKey);
    }
  }

  // Partition rows by applicability; sort each section by compensation lifecycle group (S26.3)
  const required      = sortByGroup(applicabilityRows.filter((r) => r.applicability === 'required'));
  const optional      = sortByGroup(applicabilityRows.filter((r) => r.applicability === 'optional'));
  const notApplicable = applicabilityRows.filter((r) => r.applicability === 'not_applicable');

  // Progress: required rows where modifier_status === 'approved'
  const configuredRequired = required.filter((r) => r.modifier_status === 'approved').length;

  if (applicabilityRows.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-[rgb(var(--color-border))] px-6 py-6 text-center">
        <p className="text-tok-sm text-[rgb(var(--color-text-muted))]">No modifier configuration available for this component.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">

      {/* Progress bar — only when there are required modifiers */}
      {required.length > 0 && (
        <ProgressBar configured={configuredRequired} total={required.length} />
      )}

      {/* ── REQUIRED section ── */}
      {required.length > 0 && (
        <SectionHeader
          label="Required"
          count={required.length}
          defaultOpen={true}
        >
          {required.map((row) => (
            <ChecklistRow
              key={row.modifier_type}
              row={row}
              isExpanded={expandedKey === row.modifier_type}
              onToggle={handleToggle}
              isLocked={isLocked}
              planId={planId}
              planComponentId={planComponentId}
              period={period}
              mode={mode}
              readOnly={readOnly}
              MANAGER_MAP={MANAGER_MAP}
              MODIFIER_CARDS={MODIFIER_CARDS}
              GRID_KEY_TO_MODIFIER={GRID_KEY_TO_MODIFIER}
            />
          ))}
        </SectionHeader>
      )}

      {/* ── OPTIONAL section ── */}
      {optional.length > 0 && (
        <SectionHeader
          label="Optional"
          count={optional.length}
          defaultOpen={true}
        >
          {optional.map((row) => (
            <ChecklistRow
              key={row.modifier_type}
              row={row}
              isExpanded={expandedKey === row.modifier_type}
              onToggle={handleToggle}
              isLocked={isLocked}
              planId={planId}
              planComponentId={planComponentId}
              period={period}
              mode={mode}
              readOnly={readOnly}
              MANAGER_MAP={MANAGER_MAP}
              MODIFIER_CARDS={MODIFIER_CARDS}
              GRID_KEY_TO_MODIFIER={GRID_KEY_TO_MODIFIER}
            />
          ))}
        </SectionHeader>
      )}

      {/* ── NOT APPLICABLE section — collapsed, count only ── */}
      {notApplicable.length > 0 && (
        <SectionHeader
          label="Not applicable"
          count={notApplicable.length}
          defaultOpen={false}
          badge={`${notApplicable.length} excluded by plan type`}
        >
          {notApplicable.map((row) => {
            const meta = MODIFIER_TYPE_TO_LABEL[row.modifier_type] ?? { label: row.modifier_type };
            return (
              <div key={row.modifier_type} className="flex items-center gap-3 px-4 py-2.5 opacity-50">
                <span className="h-2 w-2 rounded-full shrink-0 bg-[rgb(var(--color-border))]" />
                <span className="text-tok-sm text-[rgb(var(--color-text-muted))]">{meta.label}</span>
              </div>
            );
          })}
        </SectionHeader>
      )}

    </div>
  );
}
